<template>
    <template v-if="!$ff('new-resource-card-in-guides')">
        <div v-if="guide.data" class="guide-card">
            <div class="thumbnail-row">
                <NuxtLink :to="linkResolver(guide)" class="thumbnail-container">
                    <PrismicImage :field="guide.data.thumbnail" :imgix-params="{w: 800}" />
                </NuxtLink>
            </div>

            <div class="top-info">
                <CategoryPillList :document="guide" />
            </div>
            <NuxtLink :to="linkResolver(guide)" class="article-text">
                <h3 class="title">{{ guide.data.title }}</h3>
            </NuxtLink>
            <NuxtLink class="link" :to="linkResolver(guide)">{{ t('resources.guides.readLink') }}</NuxtLink>
        </div>
    </template>

    <template v-else>
        <ResourceCard
            v-if="guide.data"
            :document-type="guide.type"
            :image="guide.data.thumbnail"
            :title="guide.data.title"
            :excerpt="guide.data.excerpt"
            :to="linkResolver(guide)"
            :resource-categories="(guide.data?.resourceCategories as {resourceCategory: ResourceCategoryDocument}[]) ?? undefined"
            :category="guide.data.category"
            :date="releaseDate"
            :base-data-testid="baseDataTestid"
        />
    </template>
</template>

<script lang="ts" setup>
    import {computed} from 'vue';
    import type {FilledContentRelationshipField} from '@prismicio/types';
    import {PrismicImage} from '@prismicio/vue';
    import type {GuideDocument, GuideDocumentData, ResourceCategoryDocument} from '@prismicio-types';
    import {useFormat, useTranslation} from '#imports';
    import linkResolver from '~/app/prismic/link-resolver';
    import CategoryPillList from '~/components/CategoryPillList.vue';
    import ResourceCard from '~/components/ResourceCard.vue';
    import {createContentHelpers} from '~/composables/contentHelpers';

    const props = defineProps<{
        guide: GuideDocument | FilledContentRelationshipField<'guide', string, GuideDocumentData>;
    }>();
    const {t} = useTranslation();

    const {getPublicationDate} = createContentHelpers();
    const {formatDate} = useFormat();
    const releaseDate = formatDate(getPublicationDate(props.guide));

    const baseDataTestid = computed(() => props.guide.uid ?? props.guide.id);
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;

    .guide-card {
        width: auto;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-end;
        position: relative;

        .thumbnail-row {
            width: 100%;
            height: 335px;
            display: flex;
            justify-content: center;
            align-items: center;
            @media (max-width: $mobile-breakpoint) {
                height: 280px;
            }

            .thumbnail-container {
                position: relative;
                display: block;

                img {
                    max-height: 335px;
                    max-width: 100%;
                    object-fit: contain;
                    border-radius: var(--joy-core-radius-3);
                    box-shadow: var(--joy-core-elevation-4);
                    position: relative;
                    transform: rotateZ(0deg);
                    transition: all var(--joy-transition-duration-long) var(--joy-transition-timing-function);
                    z-index: 3;

                    @media (max-width: $mobile-breakpoint) {
                        max-height: 280px;
                    }
                }

                &::before {
                    content: '';
                    position: absolute;
                    background-color: var(--joy-color-neutral-0);
                    top: 0;
                    left: 0;
                    bottom: 0;
                    right: 0;
                    border-radius: var(--joy-core-radius-3);
                    box-shadow: var(--joy-core-elevation-3);
                    z-index: 2;
                    transition: all var(--joy-transition-duration-long) var(--joy-transition-timing-function);
                    opacity: 0;
                }

                &::after {
                    content: '';
                    position: absolute;
                    background-color: var(--joy-color-neutral-0);
                    z-index: 1;
                    top: 0;
                    left: 0;
                    bottom: 0;
                    right: 0;
                    border-radius: var(--joy-core-radius-3);
                    box-shadow: var(--joy-core-elevation-3);
                    z-index: 1;
                    transition: all var(--joy-transition-duration-long) var(--joy-transition-timing-function);
                    opacity: 0;
                }
            }
        }

        &:hover {
            .thumbnail-container {
                img {
                    transform: rotateZ(-4deg);
                }

                &::before {
                    opacity: 1;
                    transform: rotateZ(-2deg);
                }

                &::after {
                    opacity: 1;
                }
            }
        }

        .top-info {
            margin-top: var(--joy-core-spacing-10);
            display: flex;
            justify-content: flex-start;
            align-items: center;
        }

        .article-text {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        }

        .title {
            margin-top: var(--joy-core-spacing-5);
        }

        .link {
            @include link;
            margin-top: var(--joy-core-spacing-7);
        }
    }
</style>
